import {DataAdapter, DataObjectId} from 'yougile-model'
import {ensure, hasKey, Util} from 'yougile-util'
import {CollectionValue, isCollection} from './collections'
import {ProjectsValue, TasksValue} from './tasks-value'

export interface User {
  id?: string
  user: string
}

export const isUser = (obj: unknown): obj is User =>
  hasKey(obj, 'user') && typeof obj.user === 'string'

type UsersBasicValue =
  | User
  | {current: 'user'}
  | {assignedTo: TasksValue}
  | {responsible: TasksValue}
  | {createdBy: TasksValue}
  | {field: string}
  | {participants: ProjectsValue}
  | UsersBasicValue[]

export const isUserValue = (
  obj: unknown,
  recur = 0
): obj is UsersBasicValue => {
  if (recur > 3) {
    return false
  }
  if (!(obj instanceof Object)) {
    return false
  }
  const u = obj as UsersBasicValue
  return (
    isUser(obj) ||
    ('current' in u && u.current === 'user') ||
    'assignedTo' in u ||
    'createdBy' in u ||
    'field' in u ||
    'participants' in u ||
    (u instanceof Array && !u.some(s => !isUserValue(s, recur + 1)))
  )
}

export type UsersValue = CollectionValue<UsersBasicValue>
export const isUsersValue = (obj: unknown): obj is UsersValue =>
  isCollection(obj, isUserValue)

export const usersValueToIds = async (
  users: UsersValue,
  adapter: DataAdapter
): Promise<DataObjectId[]> => {
  const {userId} = adapter
  const userIds: DataObjectId[] = []
  const names = new Map<string, DataObjectId>()
  const emails = new Map<string, DataObjectId>()

  for (const userId of adapter.allUsers) {
    const acc = await adapter.getAccount(userId)
    if (acc?.realName) {
      names.set(acc.realName, userId)
    }
    const user = adapter.getUser(userId)
    if (user?.email) {
      emails.set(user.email, userId)
    }
  }

  const userArr = users instanceof Array ? users : [users]
  for (const user of userArr) {
    if ('user' in user && user.user) {
      const uid = Util.isEmail(user.user)
        ? emails.get(user.user)
        : names.get(user.user)
      ensure(uid, 'User not found', user)
      userIds.push(uid)
      continue
    }

    if ('current' in user && user.current === 'user') {
      userIds.push(userId)
      continue
    }

    ensure(false, 'Not supported user format', user)
  }
  return userIds
}
